/**
 * Secure API service with request signing and enhanced security
 * This replaces the basic api.js with security features
 */

import { signRequest, SecureStorage, ClientRateLimiter, sanitizeInput } from './security.js';
import { dev } from '$app/environment';
import { BASE_API_URL } from '$lib/constants/index.js';
import { env as publicEnv } from '$env/dynamic/public';

// API Configuration
const API_BASE_URL = BASE_API_URL;
const runtimeEnv = typeof process !== 'undefined' ? process.env : {};
const API_SIGNATURE_SECRET =
	publicEnv.PUBLIC_API_SIGNATURE_SECRET ||
	runtimeEnv.PUBLIC_API_SIGNATURE_SECRET ||
	runtimeEnv.API_SIGNATURE_SECRET ||
	'';
const ENABLE_REQUEST_SIGNING =
	(publicEnv.PUBLIC_ENABLE_REQUEST_SIGNING ||
		runtimeEnv.PUBLIC_ENABLE_REQUEST_SIGNING ||
		runtimeEnv.ENABLE_REQUEST_SIGNING ||
		'false') === 'true';

// Rate limiter instance
const rateLimiter = new ClientRateLimiter();

/**
 * Guard flag: prevents multiple simultaneous 401 responses from
 * all triggering a redirect at the same time.
 */
let _redirectingToLogin = false;

/**
 * Build full API URL
 * @param {string} endpoint - API endpoint
 * @returns {string} Full URL
 */
function buildUrl(endpoint) {
	// Remove leading slash if present
	const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
	return `${API_BASE_URL}${cleanEndpoint}`;
}

/**
 * Build business code header
 * @param {string|number|null|undefined} businessCode
 * @returns {Record<string, string>}
 */
function buildBusinessCodeHeader(businessCode) {
	if (!businessCode) {
		return {};
	}

	const codeValue = String(businessCode);
	const encoded =
		typeof btoa === 'function'
			? btoa(codeValue)
			: Buffer.from(codeValue, 'utf-8').toString('base64');

	return { 'X-Business-Code': encoded };
}

/**
 * Extract business code from JWT token
 * @param {string|null} token - JWT token
 * @returns {string|null} Business code
 */
function getBusinessCodeFromToken(token) {
	if (!token) return null;

	try {
		// JWT is base64url encoded, split into parts
		const parts = token.split('.');
		if (parts.length !== 3) return null;

		// Decode the payload (second part)
		const payload = parts[1];
		// Convert base64url to base64
		const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
		// Add padding if needed
		const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');

		// Decode
		const decoded = typeof atob === 'function'
			? JSON.parse(atob(padded))
			: JSON.parse(Buffer.from(padded, 'base64').toString('utf-8'));

		return decoded.business || null;
	} catch (error) {
		console.error('Error extracting business code from token:', error);
		return null;
	}
}

/**
 * Get authorization headers with automatic business code injection
 * @param {string|null} token - JWT token
 * @param {string|number|null} businessCode - Optional business code to override auto-detection
 * @param {boolean} skipBusinessCode - Skip business code header injection
 * @returns {Record<string, string>} Headers object
 */
function getAuthHeaders(token = null, businessCode = null, skipBusinessCode = false) {
	/** @type {Record<string, string>} */
	const headers = {
		'Content-Type': 'application/json'
	};

	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	// Auto-inject business code header if available and not skipped
	if (!skipBusinessCode) {
		const code = businessCode || getBusinessCodeFromToken(token);
		if (code) {
			const businessHeaders = buildBusinessCodeHeader(code);
			Object.assign(headers, businessHeaders);
		}
	}

	return headers;
}

/**
 * Custom API error that preserves the full backend response data.
 * Server actions can inspect error.data for non_field_errors, field errors, message, etc.
 */
class ApiError extends Error {
	/**
	 * @param {string} message - Human-readable error message
	 * @param {number} status - HTTP status code
	 * @param {object} data - Full JSON error response from the backend
	 */
	constructor(message, status, data) {
		super(message);
		this.name = 'ApiError';
		this.status = status;
		this.data = data;
	}
}

/**
 * Redirect to /dashboard/logout and clear stored session when the access token has expired.
 * We redirect to /dashboard/logout (NOT /login directly) because the SvelteKit session
 * lives in a server-side cookie managed by svelte-kit-cookie-session. Going straight to
 * /login leaves that cookie intact and hooks.server.js bounces the user back to the
 * dashboard. /dashboard/logout clears the cookie + session then forwards to /login.
 *
 * The _redirectingToLogin flag prevents duplicate redirects when multiple
 * parallel API calls all fail with 401 at the same time.
 */
function handleExpiredToken() {
	// Only run once even if many requests fail simultaneously
	if (_redirectingToLogin) return;
	_redirectingToLogin = true;

	SecureStorage.clear();

	if (typeof window !== 'undefined') {
		// Go through the logout route so it clears the server-side session cookie.
		// ?reason=expired tells the logout page this was an automatic session expiry.
		window.location.href = '/dashboard/logout?reason=expired';
	}
}

/**
 * Handle API errors
 * @param {Response} response - Fetch response
 * @returns {Promise<never>} Throws ApiError
 */
async function handleError(response) {
	try {
		const errorData = await response.json();
		const msg = errorData.message || errorData.detail || errorData.error || 'An error occurred';

		// ── Token expiry: force re-login ──
		if (
			response.status === 401 &&
			(errorData.code === 'token_not_valid' ||
				errorData.messages?.some(
					/** @param {{ message: string }} m */(m) => m.message === 'Token is expired'
				))
		) {
			handleExpiredToken();
			throw new ApiError('Your session has expired. Please log in again.', 401, errorData);
		}

		throw new ApiError(msg, response.status, errorData);
	} catch (e) {
		// If it's already an ApiError, re-throw it
		if (e instanceof ApiError) {
			throw e;
		}
		// Otherwise the response body wasn't valid JSON
		throw new ApiError(response.statusText || 'An error occurred', response.status, {});
	}
}

/**
 * Make secure API request
 * @param {string} endpoint - API endpoint
 * @param {RequestInit} options - Fetch options
 * @param {string|null} token - JWT token
 * @param {boolean} skipRateLimit - Skip rate limiting check
 * @returns {Promise<any>} Response data
 */
async function makeRequest(endpoint, options = {}, token = null, skipRateLimit = false, businessCode = null, skipBusinessCode = false) {
	/** @type {RequestInit} */
	const requestOptions = options;
	const url = buildUrl(endpoint);
	const urlObj = new URL(url);
	const path = `${urlObj.pathname}${urlObj.search}`;

	// Client-side rate limiting
	if (!skipRateLimit && !rateLimiter.checkLimit(endpoint, 50, 60000)) {
		throw new Error('Too many requests. Please slow down.');
	}

	// Determine if this endpoint should skip business code
	const authEndpoints = ['token/', 'token/refresh/', 'users/activate_account/', 'users/reset_password/', 'users/forgot_password/', 'create-business/', 'health/', 'subscription/'];
	const shouldSkipBusinessCode = skipBusinessCode || authEndpoints.some(authEp => endpoint.includes(authEp));

	// Build headers
	let headers = getAuthHeaders(token, businessCode, shouldSkipBusinessCode);

	// Add request signing if enabled
	if (ENABLE_REQUEST_SIGNING && API_SIGNATURE_SECRET) {
		try {
			const body = requestOptions.body || null;
			const parsedBody =
				typeof body === 'string' && body.length > 0 ? JSON.parse(body) : null;
			const signatureHeaders = await signRequest(
				requestOptions.method || 'GET',
				path,
				parsedBody,
				API_SIGNATURE_SECRET
			);
			headers = { ...headers, ...signatureHeaders };
		} catch (error) {
			console.error('Request signing failed:', error);
			// Continue without signing in development
			if (!dev) {
				throw new Error('Request signing failed');
			}
		}
	}

	// Add security headers
	headers['X-Requested-With'] = 'XMLHttpRequest';

	// Make request
	try {
		const response = await fetch(url, {
			...requestOptions,
			headers: { ...headers, ...(requestOptions.headers || {}) }
			// Note: credentials removed - we use JWT Bearer tokens instead of cookies
		});

		if (!response.ok) {
			await handleError(response);
		}

		// Handle empty responses
		const contentType = response.headers.get('content-type');
		if (contentType && contentType.includes('application/json')) {
			return await response.json();
		}

		return await response.json();
	} catch (error) {
		console.error('API Request failed:', error);
		throw error;
	}
}

/**
 * Fetch data from API (GET request)
 * @param {string} endpoint - API endpoint
 * @param {string|null} token - JWT token
 * @param {RequestInit} options - Fetch options
 * @param {string|number|null} businessCode - Optional business code
 * @returns {Promise<any>} Response data
 */
export async function fetchData(endpoint, token = null, options = {}, businessCode = null) {
	return makeRequest(endpoint, { method: 'GET', ...options }, token, false, businessCode);
}

/**
 * Post data to API
 * @param {string} endpoint - API endpoint
 * @param {string} method - HTTP method (POST, PUT, PATCH, DELETE)
 * @param {object} data - Request body data
 * @param {string|null} token - JWT token
 * @param {boolean} skipRateLimit - Skip rate limiting check
 * @param {string|number|null} businessCode - Optional business code
 * @returns {Promise<any>} Response data
 */
export async function postData(endpoint, method = 'POST', data = {}, token = null, skipRateLimit = false, businessCode = null) {
	return makeRequest(
		endpoint,
		{
			method,
			body: JSON.stringify(data)
		},
		token,
		skipRateLimit,
		businessCode
	);
}

/**
 * Put data to API
 * @param {string} endpoint - API endpoint
 * @param {object} data - Request body data
 * @param {string|null} token - JWT token
 * @param {string|number|null} businessCode - Optional business code
 * @returns {Promise<any>} Response data
 */
export async function putData(endpoint, data = {}, token = null, businessCode = null) {
	return postData(endpoint, 'PUT', data, token, false, businessCode);
}

/**
 * Delete data from API
 * @param {string} endpoint - API endpoint
 * @param {string|null} token - JWT token
 * @param {string|number|null} businessCode - Optional business code
 * @returns {Promise<any>} Response data
 */
export async function deleteData(endpoint, token = null, businessCode = null) {
	return makeRequest(endpoint, { method: 'DELETE' }, token, false, businessCode);
}

/**
 * Upload file to API
 * @param {string} endpoint - API endpoint
 * @param {FormData} formData - Form data with file
 * @param {string|null} token - JWT token
 * @param {string} [method='POST'] - HTTP method (POST or PUT)
 * @param {string|number|null} businessCode - Optional business code
 * @returns {Promise<any>} Response data
 */
export async function uploadFile(endpoint, formData, token = null, method = 'POST', businessCode = null) {

	const url = buildUrl(endpoint);
	/** @type {Record<string, string>} */
	const headers = {};

	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	// Add business code header for uploads
	const code = businessCode || getBusinessCodeFromToken(token);
	if (code) {
		const businessHeaders = buildBusinessCodeHeader(code);
		Object.assign(headers, businessHeaders);
	}

	// Add request signing if enabled
	if (ENABLE_REQUEST_SIGNING && API_SIGNATURE_SECRET) {
		try {
			const urlObj = new URL(url);
			const path = `${urlObj.pathname}${urlObj.search}`;
			const signatureHeaders = await signRequest(
				method || 'POST',
				path,
				null, // body is null because it's FormData
				API_SIGNATURE_SECRET
			);
			Object.assign(headers, signatureHeaders);
		} catch (error) {
			console.error('Request signing failed for file upload:', error);
			if (!dev) {
				throw new Error('Request signing failed');
			}
		}
	}

	// Add security headers
	headers['X-Requested-With'] = 'XMLHttpRequest';

	const response = await fetch(url, {
		method: method || 'POST',
		headers,
		body: formData
		// Note: credentials removed - we use JWT Bearer tokens instead of cookies
	});

	if (!response.ok) {
		await handleError(response);
	}

	return await response.json();
}

/**
 * Authenticate user and get token
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<object>} Token data
 */
export async function login(email, password) {
	return postData('token/', 'POST', {
		email: sanitizeInput(email),
		password
	});
}

/**
 * Refresh JWT token
 * @param {string} refreshToken - Refresh token
 * @returns {Promise<object>} New token data
 */
export async function refreshToken(refreshToken) {
	return postData(
		'token/refresh/',
		'POST',
		{ refresh: refreshToken },
		null,
		true // Skip rate limit for token refresh
	);
}

/**
 * Logout and clear session
 * @returns {Promise<void>}
 */
export async function logout() {
	// Clear secure storage
	SecureStorage.clear();

	// Clear rate limiter
	rateLimiter.clearAll();

	// Optionally call backend logout endpoint if exists
	// await postData('logout/', 'POST', {});
}

/**
 * Get current user data
 * @param {string} token - JWT token
 * @returns {Promise<object>} User data
 */
export async function getCurrentUser(token) {
	return fetchData('users/me/', token);
}

/**
 * Check API health
 * @returns {Promise<boolean>} True if API is healthy
 */
export async function checkHealth() {
	try {
		await fetchData('health/');
		return true;
	} catch {
		return false;
	}
}

export { ApiError };

export default {
	fetchData,
	postData,
	putData,
	deleteData,
	uploadFile,
	login,
	refreshToken,
	logout,
	getCurrentUser,
	checkHealth,
	ApiError
};
